import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { MapView } from '@/components/Map/MapView';
import { PaymentModal } from '@/components/PaymentModal';
import { GenerationProgressModal } from '@/components/GenerationProgressModal';
import { useAppStore } from '@/store/appStore';
import { TileCalculationService } from '@/services/tileCalculationService';
import { PRICING_TIERS, DEFAULT_IMAGERY_SOURCE } from '@/utils/constants';
import { formatCurrency, formatCompactNumber, formatFileSize } from '@/utils/formatters';
import { useAuth } from '@/providers/AuthProvider';
import { api } from '@/services/api';
import type { UserType } from '@/types';

export function AppPage() {
  const navigate = useNavigate();
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  const {
    userType,
    setUserType,
    mapView,
    setMapView,
    aoiGeometry,
    setAOIGeometry,
    isDrawingAOI,
    setIsDrawingAOI,
    minZoom,
    maxZoom,
    setMinZoom,
    setMaxZoom,
    includeNauticalOverlay,
    setIncludeNauticalOverlay,
    tileCount,
    setTileCount,
    estimatedFileSize,
    setEstimatedFileSize,
    estimatedTime,
    setEstimatedTime,
    cursorLat,
    cursorLon,
    currentZoom,
    setCursorPosition,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    clearAOI
  } = useAppStore();

  // Get auth state
  const { isAuthenticated, isLoading: authLoading, user: authUser } = useAuth();

  const queryClient = useQueryClient();

  // Fetch subscription data for authenticated users
  const { data: usage, isLoading: _usageLoading } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.getMyUsage(),
    enabled: isAuthenticated,
    retry: false,
  });

  // Calculate effective tile count (with nautical overlay = 2x)
  const effectiveTileCount = includeNauticalOverlay ? tileCount * 2 : tileCount;

  // Fetch pricing from backend when we have tiles to calculate
  const { data: backendPricing } = useQuery({
    queryKey: ['orderPricing', effectiveTileCount, includeNauticalOverlay],
    queryFn: () => api.calculateOrderPricing({
      tileCount: effectiveTileCount,
      includeNauticalOverlay,
    }),
    enabled: effectiveTileCount > 0 && isAuthenticated,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Map package slug to user type
  const mapPackageToUserType = (packageSlug?: string): UserType => {
    if (!packageSlug) return 'casual';
    const slug = packageSlug.toLowerCase();
    if (slug.includes('enterprise')) return 'enterprise';
    if (slug.includes('pro')) return 'pro';
    if (slug.includes('basic')) return 'basic';
    return 'casual';
  };

  // Compute effective user type immediately (avoids useEffect timing issues)
  const effectiveUserType = useMemo((): UserType | null => {
    // Still loading auth - no user type yet
    if (authLoading) return null;

    // Authenticated users - derive from subscription or use casual
    if (isAuthenticated) {
      const packageSlug = usage?.package?.slug || usage?.package?.name;
      return mapPackageToUserType(packageSlug);
    }

    // Unauthenticated users - check localStorage
    const storedUserType = localStorage.getItem('vortex_user_type');
    if (storedUserType && ['casual', 'basic', 'pro', 'enterprise'].includes(storedUserType)) {
      return storedUserType as UserType;
    }

    // No user type available
    return null;
  }, [authLoading, isAuthenticated, usage]);

  // Sync effectiveUserType to store (for other components that depend on it)
  useEffect(() => {
    if (effectiveUserType && effectiveUserType !== userType) {
      setUserType(effectiveUserType);
    }
  }, [effectiveUserType, userType, setUserType]);

  // Redirect unauthenticated users without a stored user type
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !effectiveUserType) {
      navigate('/select-type');
    }
  }, [authLoading, isAuthenticated, effectiveUserType, navigate]);

  // Recalculate tiles when AOI or zoom levels change
  useEffect(() => {
    if (!aoiGeometry) {
      setTileCount(0);
      setEstimatedFileSize(0);
      setEstimatedTime(0);
      return;
    }

    if (minZoom > maxZoom) {
      return;
    }

    const calculation = TileCalculationService.calculateTileCount(aoiGeometry, minZoom, maxZoom);
    setTileCount(calculation.totalTiles);

    const fileSize = TileCalculationService.estimateFileSize(calculation.totalTiles);
    setEstimatedFileSize(fileSize);

    const time = TileCalculationService.estimateGenerationTime(calculation.totalTiles);
    setEstimatedTime(time);
  }, [aoiGeometry, minZoom, maxZoom, setTileCount, setEstimatedFileSize, setEstimatedTime]);

  const handleLogout = () => {
    if (isAuthenticated) {
      // For authenticated users, go back to dashboard
      navigate('/dashboard');
    } else {
      // For legacy users, clear localStorage and go home
      localStorage.removeItem('vortex_user_type');
      navigate('/');
    }
  };

  const handleStartDrawing = () => {
    clearAOI();
    setIsDrawingAOI(true);
  };

  const handleAOIComplete = (geometry: any) => {
    setAOIGeometry(geometry);
    setIsDrawingAOI(false);

    // Save AOI center to localStorage for next session
    if (geometry && geometry.rings && geometry.rings[0]) {
      const coords = geometry.rings[0];
      let sumLat = 0;
      let sumLng = 0;

      for (const [lng, lat] of coords) {
        sumLat += lat;
        sumLng += lng;
      }

      const centerLat = sumLat / coords.length;
      const centerLng = sumLng / coords.length;

      try {
        localStorage.setItem('vortex_map_center', JSON.stringify({ lat: centerLat, lng: centerLng }));
        // Save current zoom level too
        if (mapView) {
          localStorage.setItem('vortex_map_zoom', String(mapView.getZoom()));
        }
      } catch (error) {
        console.error('Error saving map center:', error);
      }
    }
  };

  const handleClearAOI = () => {
    clearAOI();
    // Clear graphics from map will be handled by AOIDrawer component
  };

  const handlePurchase = () => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = (jobId: string) => {
    setCurrentJobId(jobId);
    setIsPaymentModalOpen(false);
    setIsProgressModalOpen(true);
  };

  // Show loading spinner only while auth is loading or no user type determined
  if (authLoading || !effectiveUserType) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {authLoading ? 'Checking authentication...' : 'Loading chart creator...'}
          </p>
        </div>
      </div>
    );
  }

  const tierInfo = PRICING_TIERS[effectiveUserType];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm z-10">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              {/* Logo */}
              <img
                src="/assets/vortex-logo.svg"
                alt="Vortex Marine"
                className="h-12"
              />
            </div>

            <div className="flex items-center space-x-4">
              {isAuthenticated && authUser && (
                <span className="text-sm text-gray-600">
                  {authUser.email}
                </span>
              )}
              <Badge variant={effectiveUserType === 'pro' ? 'success' : 'info'}>
                {tierInfo.name} {isAuthenticated ? 'Plan' : 'User'}
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                {isAuthenticated ? 'Dashboard' : 'Switch Account'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Two Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - 25% */}
        <div className="w-1/4 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Welcome Section */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Create Your Chart
              </h2>
              <p className="text-gray-600">
                Navigate the map, draw your area, and download offline charts
              </p>
            </div>

            {/* Subscription Status Panel - for authenticated users */}
            {isAuthenticated && usage && (
              <Card variant="bordered" padding="md" className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    {usage.package?.name || 'No Plan'}
                  </h3>
                  {usage.subscription ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="warning">No Subscription</Badge>
                  )}
                </div>

                {usage.subscription ? (
                  <>
                    {/* Token Balance */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Token Balance</span>
                        <span className="font-bold text-cyan-600">
                          {formatCompactNumber(usage.tokens_remaining ?? 0)} remaining
                        </span>
                      </div>

                      {/* Usage Progress Bar */}
                      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 rounded-full transition-all ${
                            (usage.usage_percentage ?? 0) > 90 ? 'bg-red-500' :
                            (usage.usage_percentage ?? 0) > 70 ? 'bg-amber-500' : 'bg-cyan-500'
                          }`}
                          style={{ width: `${Math.min(usage.usage_percentage ?? 0, 100)}%` }}
                        />
                      </div>

                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{formatCompactNumber(usage.tokens_used ?? 0)} used</span>
                        <span>{formatCompactNumber((usage.tokens_included ?? 0) + (usage.tokens_rolled_over ?? 0))} total</span>
                      </div>

                      {(usage.days_remaining ?? 0) > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          {usage.days_remaining} days until renewal
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-600 mb-3">
                      Subscribe to get monthly token allowance and save on charts
                    </p>
                    <Link
                      to="/dashboard/subscription"
                      className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors"
                    >
                      View Plans
                    </Link>
                  </div>
                )}
              </Card>
            )}

            {/* Instructions */}
            <Card variant="bordered" padding="md">
              <h3 className="font-semibold text-gray-900 mb-3">How to use:</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="font-bold text-primary-600 mr-2">1.</span>
                  Navigate to your area of interest
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-primary-600 mr-2">2.</span>
                  Click "Draw AOI" and draw a polygon
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-primary-600 mr-2">3.</span>
                  Adjust zoom levels below
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-primary-600 mr-2">4.</span>
                  Review pricing and purchase
                </li>
              </ol>
            </Card>

            {/* AOI Controls */}
            <div className="space-y-3">
              {!aoiGeometry ? (
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={handleStartDrawing}
                  disabled={isDrawingAOI}
                >
                  {isDrawingAOI ? 'Drawing... (click to finish)' : '📍 Draw AOI'}
                </Button>
              ) : (
                <div className="flex space-x-2">
                  <Button variant="outline" size="md" fullWidth onClick={handleClearAOI}>
                    Clear AOI
                  </Button>
                  <Button variant="primary" size="md" fullWidth onClick={handleStartDrawing}>
                    Redraw
                  </Button>
                </div>
              )}
            </div>

            {/* Zoom Level Selector */}
            <Card variant="bordered" padding="md">
              <h3 className="font-semibold text-gray-900 mb-4">Zoom Levels</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Zoom: {minZoom}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={minZoom}
                    onChange={(e) => setMinZoom(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">Ocean passage view</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Zoom: {maxZoom}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={maxZoom}
                    onChange={(e) => setMaxZoom(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">Harbor detail view</p>
                </div>
              </div>

              {minZoom > maxZoom && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    Min zoom must be less than max zoom
                  </p>
                </div>
              )}
            </Card>

            {/* Nautical Overlay Option */}
            <Card variant="bordered" padding="md">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Nautical Chart Overlay</h3>
                  <p className="text-xs text-gray-600">
                    Blend OpenSeaMap nautical data with satellite imagery
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNauticalOverlay}
                    onChange={(e) => setIncludeNauticalOverlay(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
              {includeNauticalOverlay && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Downloads both satellite and nautical layers (2x token count).
                    Files will be larger (PNG format) and generation will take longer.
                  </p>
                </div>
              )}
            </Card>

            {/* Tile Count & Pricing */}
            <Card variant="elevated" padding="md" className="bg-gradient-to-br from-primary-50 to-secondary-50">
              <h3 className="font-semibold text-gray-900 mb-4">Pricing Breakdown</h3>

              {tileCount === 0 ? (
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">📐</div>
                  <p className="text-sm text-gray-600">Draw an AOI to see pricing</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Token Count</span>
                    <span className="font-bold text-lg text-primary-600">
                      {formatCompactNumber(tileCount)}
                    </span>
                  </div>
                  {includeNauticalOverlay && (
                    <>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-blue-600">+ Nautical tokens</span>
                        <span className="text-blue-600 font-semibold">
                          {formatCompactNumber(tileCount)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                        <span className="text-gray-700 font-semibold">Total Tokens Required</span>
                        <span className="font-bold text-primary-600">
                          {formatCompactNumber(effectiveTileCount)}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Est. File Size</span>
                    <span className="font-semibold text-gray-900">
                      {formatFileSize(estimatedFileSize)}
                    </span>
                  </div>

                  {/* Subscription Token Usage (for authenticated users with subscriptions) */}
                  {isAuthenticated && backendPricing && backendPricing.tokensFromQuota > 0 && (
                    <div className="pt-3 border-t border-gray-200 space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-600">From Subscription</span>
                        <span className="font-semibold text-green-600">
                          -{formatCompactNumber(backendPricing.tokensFromQuota)} tokens
                        </span>
                      </div>
                      {backendPricing.tokensOverage > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-amber-600">Overage ({formatCompactNumber(backendPricing.tokensOverage)} tokens)</span>
                          <span className="font-semibold text-amber-600">
                            {formatCurrency(backendPricing.tilesPrice / 100)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pricing */}
                  <div className="pt-3 border-t border-gray-300 space-y-2">
                    {isAuthenticated && backendPricing ? (
                      <>
                        {backendPricing.baseFee > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Base Fee</span>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(backendPricing.baseFee / 100)}
                            </span>
                          </div>
                        )}
                        {backendPricing.tokensFromQuota === 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Tokens Cost</span>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(backendPricing.tilesPrice / 100)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                          <span className="font-bold text-gray-900">Total</span>
                          {backendPricing.total === 0 ? (
                            <span className="font-bold text-2xl text-green-600">
                              FREE
                            </span>
                          ) : (
                            <span className="font-bold text-2xl text-primary-600">
                              {formatCurrency(backendPricing.total / 100)}
                            </span>
                          )}
                        </div>
                        {backendPricing.total === 0 && backendPricing.tokensFromQuota > 0 && (
                          <p className="text-xs text-green-600 text-center">
                            Covered by your {usage?.package?.name} subscription
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Fallback pricing for non-authenticated or loading state */}
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Base Fee</span>
                          <span className="font-semibold text-gray-900">$50.00</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Tokens Cost</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency((effectiveTileCount / 1000) * 0.40)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                          <span className="font-bold text-gray-900">Total</span>
                          <span className="font-bold text-2xl text-primary-600">
                            {formatCurrency(50 + (effectiveTileCount / 1000) * 0.40)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Remaining quota after this order */}
                  {isAuthenticated && usage?.subscription && backendPricing && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">After this order</span>
                        <span className={`font-semibold ${
                          (usage.tokens_remaining - backendPricing.tokensFromQuota) < 0
                            ? 'text-amber-600'
                            : 'text-green-600'
                        }`}>
                          {formatCompactNumber(Math.max(0, usage.tokens_remaining - backendPricing.tokensFromQuota))} tokens remaining
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                variant="accent"
                size="lg"
                fullWidth
                disabled={tileCount === 0 || minZoom > maxZoom}
                onClick={handlePurchase}
              >
                💳 Purchase & Download
              </Button>
            </div>

            {/* Info Note */}
            {tileCount === 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Click "Draw AOI" above, then click on the map to draw your area.
                  Double-click to finish.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - 75% */}
        <div className="flex-1 relative bg-gray-100">
          {/* Map */}
          <MapView
            onMapLoad={setMapView}
            onCursorMove={setCursorPosition}
            onAOIComplete={handleAOIComplete}
            isDrawing={isDrawingAOI}
          />

          {/* Cursor Position Display (Bottom Right) */}
          <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-gray-200 z-[9999]">
            <div className="flex items-center space-x-4 text-sm font-mono">
              <div>
                <span className="text-gray-500">Lat:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {cursorLat.toFixed(6)}°
                </span>
              </div>
              <div className="w-px h-4 bg-gray-300"></div>
              <div>
                <span className="text-gray-500">Lon:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {cursorLon.toFixed(6)}°
                </span>
              </div>
              <div className="w-px h-4 bg-gray-300"></div>
              <div>
                <span className="text-gray-500">Zoom:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {currentZoom}
                </span>
              </div>
            </div>
          </div>

          {/* Drawing Indicator */}
          {isDrawingAOI && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-6 py-3 rounded-lg shadow-lg animate-pulse">
              <p className="font-semibold">Click on the map to draw your area. Double-click to finish.</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        userType={effectiveUserType}
        tileCount={tileCount}
        fileSize={estimatedFileSize}
        estimatedTime={estimatedTime}
        minZoom={minZoom}
        maxZoom={maxZoom}
        aoiGeometry={aoiGeometry}
        includeNauticalOverlay={includeNauticalOverlay}
        imagerySource={DEFAULT_IMAGERY_SOURCE as 'sentinel2' | 'google'}
        isAuthenticated={isAuthenticated}
        usage={usage}
        backendPricing={backendPricing}
        onOrderComplete={() => {
          // Invalidate usage query to refresh token balance
          queryClient.invalidateQueries({ queryKey: ['usage'] });
        }}
      />

      {/* Generation Progress Modal */}
      <GenerationProgressModal
        isOpen={isProgressModalOpen}
        onClose={() => setIsProgressModalOpen(false)}
        jobId={currentJobId}
        isOrderId={isAuthenticated}
      />
    </div>
  );
}
